/**
 * /accounting/reports/job-profitability — per-job P&L.
 *
 * The one report a remodeler actually opens. Derived live from the GL — every
 * line is already tagged with `jobId` + an account subtype, so there's no
 * separate data store, no drift.
 */
import { useMemo, useState } from "react";
import { useLogReadOnMount } from "@/hooks/useLogReadOnMount";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpDown, Download } from "lucide-react";
import { CockpitPage, CockpitSection } from "@/components/cockpit/CockpitShell";
import { BooksNavRail } from "@/components/accounting/BooksNavRail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import { useAppData } from "@/data/AppDataContext";
import { getJobProfitability, type JobProfitabilityRow } from "@/data/accounting/reports/jobProfitability";
import { exportCsv } from "@/lib/csvExport";

const fmt = (n: number) =>
  `$${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtPct = (n: number | null) =>
  n === null ? "—" : `${(n * 100).toFixed(1)}%`;

type SortKey = "status" | "revenue" | "grossMargin" | "grossMarginPct" | "wipBalance" | "forecastMargin";

const STATUS_TONE: Record<JobProfitabilityRow["status"], string> = {
  in_progress: "bg-amber-500/15 text-amber-700 border-amber-500/30",
  completed: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
  no_activity: "bg-muted text-muted-foreground border-border",
};
const STATUS_LABEL: Record<JobProfitabilityRow["status"], string> = {
  in_progress: "In progress",
  completed: "Completed",
  no_activity: "No activity",
};

export default function JobProfitabilityPage() {
  useLogReadOnMount("job-profitability", { sensitivity: "margin" });
  const { journalEntries, accounts } = useGeneralLedger();
  const { jobs } = useAppData();
  const [search, setSearch] = useState("");
  const [includeCompleted, setIncludeCompleted] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>("revenue");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const customerNameByJobId = useMemo(
    () => Object.fromEntries(jobs.map(j => [j.id, j.customerName])),
    [jobs],
  );
  const jobNumberByJobId = useMemo(
    () => Object.fromEntries(jobs.map(j => [j.id, j.jobNumber || j.id])),
    [jobs],
  );

  const report = useMemo(
    () => getJobProfitability({ journalEntries, accounts, customerNameByJobId }),
    [journalEntries, accounts, customerNameByJobId],
  );

  const rows = useMemo(() => {
    let r = report.rows;
    if (!includeCompleted) r = r.filter(x => x.status !== "completed");
    if (search.trim()) {
      const s = search.toLowerCase();
      r = r.filter(x =>
        x.jobId.toLowerCase().includes(s) ||
        (jobNumberByJobId[x.jobId] ?? "").toLowerCase().includes(s) ||
        (x.customerName ?? "").toLowerCase().includes(s));
    }
    const dir = sortDir === "asc" ? 1 : -1;
    return [...r].sort((a, b) => {
      const av = a[sortKey] ?? -Infinity;
      const bv = b[sortKey] ?? -Infinity;
      if (typeof av === "string" && typeof bv === "string") return av.localeCompare(bv) * dir;
      return ((av as number) - (bv as number)) * dir;
    });
  }, [report.rows, includeCompleted, search, sortKey, sortDir, jobNumberByJobId]);

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("desc"); }
  };

  const exportRows = () => {
    exportCsv(
      `job-profitability-${new Date().toISOString().slice(0, 10)}.csv`,
      ["Job", "Customer", "Status", "Revenue", "Material", "Labor", "Total Cost", "Gross Margin", "Margin %", "WIP", "Deferred Rev", "Forecast Margin", "Entries", "Last Activity"],
      rows.map(r => [
        jobNumberByJobId[r.jobId] ?? r.jobId, r.customerName ?? "", STATUS_LABEL[r.status],
        r.revenue, r.costMaterial, r.costLabor, r.totalCost,
        r.grossMargin, r.grossMarginPct === null ? "" : `${(r.grossMarginPct * 100).toFixed(1)}%`,
        r.wipBalance, r.deferredRevenue, r.forecastMargin,
        r.entryCount, r.lastActivityAt ?? "",
      ]),
    );
  };

  return (
    <CockpitPage>
      <BooksNavRail />
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <Link to="/accounting/reports" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2">
            <ArrowLeft className="h-3 w-3" /> Back to Financial Reports
          </Link>
          <h1 data-tour-id="job-profitability-table" className="text-2xl font-semibold tracking-tight">Job Profitability</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Revenue, material, labor, and margin per job — derived live from posted ledger activity.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={exportRows}>
          <Download className="h-4 w-4 mr-1" /> Export CSV
        </Button>
      </div>

      {/* Totals strip */}
      <CockpitSection>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Tile label="Active jobs" value={`${report.totals.jobCount}`} />
          <Tile label="Revenue" value={fmt(report.totals.revenue)} />
          <Tile label="Material" value={fmt(report.totals.costMaterial)} />
          <Tile label="Labor" value={fmt(report.totals.costLabor)} />
          <Tile label="Gross margin" value={fmt(report.totals.grossMargin)} tone={report.totals.grossMargin >= 0 ? "pos" : "neg"} />
          <Tile label="Margin %" value={fmtPct(report.totals.grossMarginPct)} tone={(report.totals.grossMarginPct ?? 0) >= 0.3 ? "pos" : (report.totals.grossMarginPct ?? 0) >= 0.15 ? "warn" : "neg"} />
        </div>
        <div className="mt-3 text-xs text-muted-foreground">
          Forecast margin (after WIP release): <span className="font-medium tabular-nums text-foreground">{fmt(report.totals.forecastMargin)}</span> ·
          {" "}WIP outstanding: <span className="font-medium tabular-nums text-foreground">{fmt(report.totals.wipBalance)}</span> ·
          {" "}Deferred revenue: <span className="font-medium tabular-nums text-foreground">{fmt(report.totals.deferredRevenue)}</span>
        </div>
      </CockpitSection>

      <CockpitSection>
        <div className="rounded-md border border-border bg-card">
          <div className="flex items-center justify-between p-3 border-b border-border">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIncludeCompleted(v => !v)}
                className={`text-xs px-2 py-1 rounded-sm border ${
                  includeCompleted ? "bg-primary/15 text-primary ring-1 ring-primary/30 border-transparent" : "border-border hover:bg-accent/40"
                }`}
              >
                Include completed
              </button>
              <span className="text-xs text-muted-foreground">{rows.length} job{rows.length === 1 ? "" : "s"}</span>
            </div>
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search job / customer…" className="h-7 text-xs w-56" />
          </div>
          <div className="overflow-auto max-h-[640px]">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-card border-b border-border">
                <tr className="text-muted-foreground">
                  <Th>Job</Th>
                  <Th>Customer</Th>
                  <Th sortable onClick={() => toggleSort("status")} active={sortKey === "status"}>Status</Th>
                  <Th sortable onClick={() => toggleSort("revenue")} active={sortKey === "revenue"} className="text-right">Revenue</Th>
                  <Th className="text-right">Material</Th>
                  <Th className="text-right">Labor</Th>
                  <Th sortable onClick={() => toggleSort("grossMargin")} active={sortKey === "grossMargin"} className="text-right">Gross margin</Th>
                  <Th sortable onClick={() => toggleSort("grossMarginPct")} active={sortKey === "grossMarginPct"} className="text-right">Margin %</Th>
                  <Th sortable onClick={() => toggleSort("wipBalance")} active={sortKey === "wipBalance"} className="text-right">WIP</Th>
                  <Th sortable onClick={() => toggleSort("forecastMargin")} active={sortKey === "forecastMargin"} className="text-right">Forecast</Th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">
                    No job activity matches the current filters.
                  </td></tr>
                )}
                {rows.map(r => (
                  <tr key={r.jobId} className="border-b border-border last:border-b-0 hover:bg-accent/30">
                    <td className="p-2 font-mono">
                      <Link to={`/accounting/reports/job-profitability/${r.jobId}`} className="hover:underline text-primary">
                        {jobNumberByJobId[r.jobId] ?? r.jobId}
                      </Link>
                    </td>
                    <td className="p-2 truncate max-w-[180px]" title={r.customerName ?? ""}>{r.customerName ?? "—"}</td>
                    <td className="p-2">
                      <Badge variant="outline" className={`text-[10px] font-normal ${STATUS_TONE[r.status]}`}>
                        {STATUS_LABEL[r.status]}
                      </Badge>
                    </td>
                    <td className="p-2 text-right tabular-nums">{fmt(r.revenue)}</td>
                    <td className="p-2 text-right tabular-nums text-muted-foreground">{fmt(r.costMaterial)}</td>
                    <td className="p-2 text-right tabular-nums text-muted-foreground">{fmt(r.costLabor)}</td>
                    <td className={`p-2 text-right tabular-nums font-medium ${r.grossMargin < 0 ? "text-destructive" : ""}`}>
                      {fmt(r.grossMargin)}
                    </td>
                    <td className={`p-2 text-right tabular-nums ${
                      r.grossMarginPct === null ? "text-muted-foreground" :
                      r.grossMarginPct >= 0.3 ? "text-emerald-600" :
                      r.grossMarginPct >= 0.15 ? "text-amber-600" : "text-rose-600"
                    }`}>
                      {fmtPct(r.grossMarginPct)}
                    </td>
                    <td className="p-2 text-right tabular-nums text-muted-foreground">{fmt(r.wipBalance)}</td>
                    <td className={`p-2 text-right tabular-nums ${r.forecastMargin < 0 ? "text-destructive" : "text-muted-foreground"}`}>
                      {fmt(r.forecastMargin)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </CockpitSection>

      <div className="text-[11px] text-muted-foreground text-center mt-4">
        Gross margin = revenue − (material + labor). Forecast margin = gross margin − outstanding WIP.
      </div>
    </CockpitPage>
  );
}

function Tile({ label, value, tone }: { label: string; value: string; tone?: "pos" | "neg" | "warn" }) {
  const toneClass = tone === "pos" ? "text-emerald-600" : tone === "neg" ? "text-rose-600" : tone === "warn" ? "text-amber-600" : "";
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${toneClass}`}>{value}</div>
    </div>
  );
}

function Th({
  children, sortable, onClick, active, className = "",
}: {
  children: React.ReactNode;
  sortable?: boolean;
  onClick?: () => void;
  active?: boolean;
  className?: string;
}) {
  return (
    <th className={`p-2 font-medium text-left ${className}`}>
      {sortable ? (
        <button onClick={onClick} className={`inline-flex items-center gap-1 hover:text-foreground ${active ? "text-foreground" : ""}`}>
          {children} <ArrowUpDown className="h-3 w-3" />
        </button>
      ) : children}
    </th>
  );
}
